import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Crosshair, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { coinToRp, getGPS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import RegionSelect from "@/components/RegionSelect";
import { MapPicker } from "@/components/Maps";
import { PageHead } from "@/components/common";

export function JobFields({ form, setForm, categories }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const gps = async () => {
    try {
      setForm({ ...form, point: await getGPS() });
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div className="space-y-4">
      <label className="block"><span className="text-xs font-semibold text-slate-600">Judul Pekerjaan</span>
        <input data-testid="job-title-input" className="field mt-1" placeholder="Contoh: Perbaiki AC tidak dingin" value={form.title} onChange={set("title")} required /></label>
      <label className="block"><span className="text-xs font-semibold text-slate-600">Kategori Pekerjaan</span>
        <select data-testid="job-category-select" className="field mt-1" value={form.category} onChange={set("category")} required>
          <option value="">Pilih kategori</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select></label>
      <label className="block"><span className="text-xs font-semibold text-slate-600">Deskripsi (opsional)</span>
        <textarea data-testid="job-description-input" className="field mt-1 h-20 py-2" value={form.description} onChange={set("description")} placeholder="Jelaskan detail kebutuhan Anda" /></label>
      <div><p className="text-xs font-semibold text-slate-600 mb-1">Alamat Pekerjaan</p>
        <RegionSelect value={form.address} onChange={(address) => setForm({ ...form, address })} /></div>
      <label className="block"><span className="text-xs font-semibold text-slate-600">Biaya Jasa / Tarif (koin)</span>
        <input data-testid="job-rate-input" type="number" min="1" className="field mt-1 font-mono" value={form.price_coins} onChange={set("price_coins")} required />
        <span className="text-xs text-slate-500">= {coinToRp(Number(form.price_coins) || 0)} (1 koin = Rp1.000)</span></label>
      <label data-testid="job-bring-tools" className={`flex items-start gap-3 rounded-2xl border-2 p-3 cursor-pointer transition-colors ${form.bring_tools ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"}`}>
        <input data-testid="job-bring-tools-checkbox" type="checkbox" checked={!!form.bring_tools} onChange={(e) => setForm({ ...form, bring_tools: e.target.checked })} className="w-5 h-5 mt-0.5 accent-emerald-600" />
        <span><span className="block text-sm font-bold">Wajib Membawa Alat Kerja</span><span className="block text-xs text-slate-500">Worker harus membawa peralatan sendiri untuk pekerjaan ini</span></span>
      </label>
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold text-slate-600">Titik Lokasi (ketuk peta)</p>
          <button type="button" data-testid="job-use-gps-btn" onClick={gps} className="text-xs font-bold text-emerald-700 flex items-center gap-1"><Crosshair className="w-3.5 h-3.5" /> Gunakan GPS</button>
        </div>
        <MapPicker value={form.point} onChange={(point) => setForm({ ...form, point })} />
        {form.point && <p className="text-[11px] font-mono text-slate-500 mt-1">{form.point.lat.toFixed(5)}, {form.point.lng.toFixed(5)}</p>}
      </div>
    </div>
  );
}

export function jobPayload(form) {
  if (!form.point) throw new Error("Tentukan titik lokasi pada peta");
  if (!form.address.province || !form.address.regency || !form.address.district || !form.address.village || !form.address.street)
    throw new Error("Lengkapi alamat pekerjaan");
  return { title: form.title, category: form.category, description: form.description, address: form.address,
    lat: form.point.lat, lng: form.point.lng, price_coins: Number(form.price_coins), bring_tools: !!form.bring_tools };
}

export function useJobForm(user) {
  const [form, setForm] = useState({ title: "", category: "", description: "", address: user.address || {}, price_coins: 100,
    point: user.location ? { lat: user.location.lat, lng: user.location.lng } : null });
  return [form, setForm];
}

export default function JobForm() {
  const { user, settings } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useJobForm(user);
  const [busy, setBusy] = useState(false);
  const loc = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/jobs", jobPayload(form));
      toast.success("Pekerjaan berhasil diposting");
      nav(`/jobs/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(err.response ? errMsg(err) : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="job-post-form" onSubmit={submit}>
      <PageHead title="Posting Pekerjaan" sub="Worker terdekat akan mengirim penawaran harga" />
      <JobFields form={form} setForm={setForm} categories={settings.categories || []} />
      <p className="mt-4 text-xs text-blue-800 bg-blue-50 rounded-xl px-3 py-2 flex gap-2"><ShieldCheck className="w-4 h-4 shrink-0" />Koin baru ditahan (escrow) saat Anda memilih worker, dan diteruskan setelah pekerjaan Anda konfirmasi selesai.</p>
      <button data-testid="job-submit-btn" disabled={busy}
        className="mt-5 w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Posting Pekerjaan
      </button>
    </form>
  );
}
